import { type Ref, ref, nextTick } from 'vue'
import { ConfirmInterface } from './type'

export class Confirm {
  private readonly defaultValue: ConfirmInterface = {
    isShow: false,
    message: null,
    highlightText: null,
    cancelText: null,
    confirmText: null,
    isRequiredUpdateModal: false,
    onCancel: () => {},
    onConfirm: () => {},
  }

  private confirm = ref<ConfirmInterface>({ ...this.defaultValue })
  private clickOutsideHandler?: (event: MouseEvent) => void
  private keydownHandler?: (event: KeyboardEvent) => void
  private isTransitioning = false

  get(): Ref<ConfirmInterface> {
    return this.confirm
  }

  async show({
    message,
    highlightText,
    cancelText = '닫기',
    confirmText = '확인',
    isRequiredUpdateModal = false,
    onCancel = (): void => {},
    onConfirm = (): void => {},
  }: {
    message: string
    highlightText?: string
    cancelText?: string
    confirmText?: string
    isRequiredUpdateModal?: boolean
    onCancel?: () => void | Promise<void>
    onConfirm?: () => void | Promise<void>
  }): Promise<void> {
    if (this.isTransitioning) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return this.show({
        message,
        highlightText,
        cancelText,
        confirmText,
        isRequiredUpdateModal,
        onCancel,
        onConfirm,
      })
    }

    this.removeEventListeners()

    this.confirm.value = {
      isShow: true,
      message,
      highlightText,
      cancelText,
      confirmText,
      isRequiredUpdateModal,
      onCancel: async (): Promise<void> => {
        this.isTransitioning = true
        this.close()
        await onCancel()
      },
      onConfirm: async (): Promise<void> => {
        if (isRequiredUpdateModal) {
          // 필수 업데이트 모달은 닫지 않음
          await onConfirm()
        } else {
          this.isTransitioning = true // 닫힐 때만 플래그 활성화
          this.close()
          await onConfirm()
        }
      },
    }

    await nextTick()
    if (isRequiredUpdateModal) {
      this.removeEventListeners()
      return
    }
    this.addEventListeners()
  }

  private addEventListeners(): void {
    // ESC 핸들러
    this.keydownHandler = (event: KeyboardEvent): void => {
      // isRequiredUpdateModal이 true면 ESC로도 닫히지 않음
      if (
        event.key === 'Escape' &&
        !this.isTransitioning &&
        !this.confirm.value.isRequiredUpdateModal
      ) {
        this.close()
      }
    }
    window.addEventListener('keydown', this.keydownHandler)

    // 바깥 클릭 핸들러
    this.clickOutsideHandler = (event: MouseEvent): void => {
      // isRequiredUpdateModal이 true면 외부 클릭 무시
      if (this.confirm.value.isRequiredUpdateModal) return

      const dialogElement = document.querySelector('.global-confirm-dialog')
      if (dialogElement && !dialogElement.contains(event.target as Node) && !this.isTransitioning) {
        this.close()
      }
    }
    setTimeout(() => {
      document.addEventListener('click', this.clickOutsideHandler!)
    }, 100)
  }

  private removeEventListeners(): void {
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler)
      this.keydownHandler = undefined
    }
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
      this.clickOutsideHandler = undefined
    }
  }

  private close(): void {
    this.removeEventListeners()
    this.confirm.value.isShow = false
    setTimeout(() => {
      this.confirm.value = { ...this.defaultValue }
      this.isTransitioning = false
    }, 300)
  }
}
